import { Dialog, DialogPanel } from '@headlessui/react';
import IconSvg from 'src/assets/icons/IconSvg';
import { useWallet } from 'src/hooks/useWallet';
import { useOrdinals, OrdinalToken } from 'src/hooks/useOrdinals';

interface Props {
  isOpen: boolean;
  close: () => void;
  onSelect: (token: OrdinalToken) => void;
}

const ChoseOriginalPrizeModal = ({ isOpen, close, onSelect }: Props) => {
  const { ordinalsAddress, paymentAddress, isConnected } = useWallet();
  const walletAddress = ordinalsAddress?.address || paymentAddress?.address;
  const {
    tokens,
    collections,
    isLoading,
    error,
    selectedCollection,
    setSelectedCollection,
  } = useOrdinals(isOpen && isConnected ? walletAddress : undefined);

  const handleSelect = (token: OrdinalToken) => {
    onSelect(token);
    close();
  };

  return (
    <Dialog open={isOpen} as="div" className="relative z-10 focus:outline-none" onClose={close}>
      <div className="overflow-y-auto fixed inset-0 w-screen bg-[#1B1B1F99] backdrop-blur-sm xl:top-[80px] top-[68px] xl:bottom-[61px] bottom-[78px] custom-scrollbar">
        <div className="flex justify-center items-center p-4 min-h-full">
          <DialogPanel
            transition
            className="relative p-5 max-w-[1564px] w-full rounded-lg bg-grayscale-900 xl:p-10 h-fit"
          >
            <button className="hidden absolute top-5 right-5 xl:block" onClick={close}>
              <IconSvg icon="closeM" className="text-grayscale-400" />
            </button>

            <div className="flex justify-between items-center mb-4 md:mb-6">
              <h3 className="text-brand-magnolia text-[22px] xl:text-[32px]">
                Choose an Ordinal Prize
              </h3>

              <button className="xl:hidden" onClick={close}>
                <IconSvg icon="closeM" className="text-grayscale-400" />
              </button>
            </div>

            {!isConnected && (
              <div className="flex flex-col gap-4 items-center justify-center py-20">
                <IconSvg icon="infoM" className="text-grayscale-400 w-12 h-12" />
                <p className="text-lg text-grayscale-400">
                  Connect your wallet to view your ordinals
                </p>
              </div>
            )}

            {isConnected && isLoading && (
              <div className="flex flex-col gap-4 items-center justify-center py-20">
                <div className="w-8 h-8 border-2 border-brand-tangerine border-t-transparent rounded-full animate-spin" />
                <p className="text-grayscale-400">Loading your ordinals...</p>
              </div>
            )}

            {isConnected && error && (
              <div className="flex flex-col gap-4 items-center justify-center py-20">
                <IconSvg icon="reportM" className="text-system-error w-12 h-12" />
                <p className="text-system-error">{error}</p>
              </div>
            )}

            {isConnected && !isLoading && !error && tokens.length === 0 && collections.length === 0 && (
              <div className="flex flex-col gap-4 items-center justify-center py-20">
                <IconSvg icon="infoM" className="text-grayscale-400 w-12 h-12" />
                <p className="text-lg text-grayscale-400">No ordinals found in your wallet</p>
              </div>
            )}

            {isConnected && !isLoading && !error && (tokens.length > 0 || collections.length > 0) && (
              <div className="flex gap-3 max-h-[472px] md:max-h-[550px] 3xl:max-h-[652px] w-full md:flex-row flex-col">
                <div className="p-4 space-y-4 rounded-lg bg-brand-black h-fit flex-shrink-0">
                  <div className="text-sm font-medium text-grayscale-400">Collections Owned</div>

                  <div className="flex overflow-auto flex-row gap-x-3 gap-y-2 pb-3 -mb-3 md:flex-col custom-scrollbar">
                    <button
                      onClick={() => setSelectedCollection(null)}
                      className={`flex gap-3 items-center md:w-[292px] text-left rounded-lg p-2 transition-colors ${
                        selectedCollection === null
                          ? 'bg-grayscale-700'
                          : 'hover:bg-grayscale-800'
                      }`}
                    >
                      <div className="w-10 h-10 bg-grayscale-700 rounded-full flex items-center justify-center flex-shrink-0">
                        <IconSvg icon="gridS" className="text-grayscale-400" />
                      </div>

                      <div className="hidden md:flex gap-1 items-center">
                        <div className="text-sm font-semibold text-brand-magnolia">All</div>
                        <span className="text-xs text-grayscale-400">
                          ({tokens.length})
                        </span>
                      </div>
                    </button>

                    {collections.map((collection) => (
                      <button
                        key={collection.symbol}
                        onClick={() => setSelectedCollection(collection.symbol)}
                        className={`flex gap-3 items-center md:w-[292px] text-left rounded-lg p-2 transition-colors ${
                          selectedCollection === collection.symbol
                            ? 'bg-grayscale-700'
                            : 'hover:bg-grayscale-800'
                        }`}
                      >
                        {collection.imageURI ? (
                          <img
                            src={collection.imageURI}
                            alt={collection.name}
                            className="w-10 h-10 rounded-full object-cover flex-shrink-0"
                          />
                        ) : (
                          <div className="w-10 h-10 bg-grayscale-600 rounded-full flex-shrink-0" />
                        )}

                        <div className="hidden md:flex gap-1 items-center min-w-0">
                          <div className="text-sm font-semibold text-brand-magnolia truncate">
                            {collection.name}
                          </div>
                          <span className="text-xs text-grayscale-400 flex-shrink-0">
                            ({collection.count})
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid gap-3 xl:gap-[28px] grid-cols-[repeat(auto-fill,minmax(200px,1fr))] overflow-y-auto custom-scrollbar w-full md:-mr-3 md:pr-3 -mr-2 pr-2">
                  {tokens.map((token) => (
                    <button
                      key={token.id}
                      onClick={() => handleSelect(token)}
                      className="p-3 pb-5 rounded-lg bg-brand-black text-left hover:ring-2 hover:ring-brand-tangerine transition-all"
                    >
                      {token.contentPreviewURI ? (
                        <img
                          src={token.contentPreviewURI}
                          alt={token.meta?.name || ''}
                          className="h-[200px] w-full object-cover rounded mb-4"
                          loading="lazy"
                        />
                      ) : (
                        <div className="h-[200px] w-full bg-grayscale-700 rounded mb-4 flex items-center justify-center">
                          <IconSvg icon="infoM" className="text-grayscale-500 w-8 h-8" />
                        </div>
                      )}

                      <h5 className="text-brand-magnolia text-sm truncate">
                        {token.meta?.name || `#${token.inscriptionNumber}`}
                      </h5>

                      {token.collection && (
                        <p className="text-xs text-grayscale-400 truncate mt-1">
                          {token.collection.name}
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default ChoseOriginalPrizeModal;
